import Vue from 'vue';
import VueRouter from 'vue-router';

import { SHORT_PROJECT_NAME } from '@config/application';
import { MAIN_ROUTE_NAME } from '@config/routeInformation';

import { JWTService } from '@services/jwt.service';

import store from '@store';

import {
  AUTH_NAMESPACE,
  GENERATE_TOKEN_FROM_URL,
  SET_RETURN_URL,
} from '@store/modules/auth.types';

import {
  LOCATIONS_NAMESPACE,
  GET_LOCATIONS,
} from '@store/modules/locations.types';

const AppLogin = () => import('@components/appLogin');

Vue.use(VueRouter);

const routes = [
  {
    path: '/login',
    name: 'login',
    components: {
      login: AppLogin,
    },
    meta: {
      title: 'Login',
    },
  },
  {
    path: '/403',
    name: 'forbidden',
    meta: {
      title: 'Forbidden',
    },
  },
  {
    path: '/404',
    name: 'error404',
    meta: {
      title: 'Page not found',
    },
  },
  {
    path: '*',
    redirect: '/404',
  },
];

// Automatic registration of routes from @modules
const requireRoute = require.context('@modules/', true, /\/route$/);
requireRoute.keys().forEach((key) => {
  const route = requireRoute(key).default || [];
  routes.push(...route);
});

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

const getSuffix = () => (store ? store.state[LOCATIONS_NAMESPACE].suffix : '');

let isInitStep = true;
let isInitAppLoading = false;

const initializeApp = async ({ name: routeName, query: routeQuery }) => {
  isInitAppLoading = true;

  try {
    const generatedRouteQuery = await store.dispatch(
      `${AUTH_NAMESPACE}/${GENERATE_TOKEN_FROM_URL}`,
      { query: { ...routeQuery } },
    );

    await store.dispatch(
      `${LOCATIONS_NAMESPACE}/${GET_LOCATIONS}`,
      { suffix: generatedRouteQuery.suffix || '' },
    );

    generatedRouteQuery.suffix = getSuffix() || undefined;

    return Promise.resolve({
      query: routeName === 'login'
        ? { name: MAIN_ROUTE_NAME, query: { suffix: generatedRouteQuery.suffix } }
        : generatedRouteQuery,
    });
  } catch (e) {
    const { response: { status } = {} } = e || {};

    return Promise.resolve({ isInvalidAuth: true, isForbidden: status === 403 });
  } finally {
    isInitStep = false;
    isInitAppLoading = false;
  }
};

const logout = (route, isForbidden, next) => {
  if (route.name !== 'login' && route.name !== 'forbidden') {
    store.commit(
      `${AUTH_NAMESPACE}/${SET_RETURN_URL}`,
      `${window.location.origin}${route.fullPath}`,
    );
  }

  document.title = `${route.meta.title} | ${SHORT_PROJECT_NAME}`;

  next(
    route.name !== 'login' && route.name !== 'forbidden'
      ? { name: isForbidden ? 'forbidden' : 'login' }
      : undefined,
  );
};

router.beforeEach(async (to, from, next) => {
  if (isInitAppLoading) {
    return;
  }

  let query = null;

  if (isInitStep) {
    const { isInvalidAuth, isForbidden, query: q } = await initializeApp(to);

    if (isInvalidAuth) {
      logout(to, isForbidden, next);
      return;
    }

    if (q) {
      query = q;
    }
  }

  if (!JWTService.getToken()) {
    logout(to, false, next);
    return;
  }

  if (query) {
    next({ ...to, query });
    return;
  }

  if (to.name === 'error404' || to.name === 'forbidden') {
    next();
    return;
  }

  if (to.name === 'login') {
    next({
      name: MAIN_ROUTE_NAME,
      query: {
        suffix: getSuffix(),
      },
    });
    return;
  }

  document.title = `${to.meta.title} | ${SHORT_PROJECT_NAME}`;

  next();
});

router.onError(({ message: m }) => {
  if (/loading chunk \d* failed./i.test(m)) {
    window.location.reload();
  }
});

export const createRouter = () => router;

export default router;
